import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { usePrice } from '../hooks/usePrice';

export default function Home() {
  const { formatPrice } = usePrice();
  const { data: productsData } = useQuery({
    queryKey: ['featured-products'],
    queryFn: () => api.get('/products?limit=8&sortBy=salesCount'),
  });

  const { data: sellersData } = useQuery({
    queryKey: ['featured-sellers'],
    queryFn: () => api.get('/sellers?limit=4&sortBy=sales'),
  });

  const products = (productsData as any)?.data?.products || [];
  const sellers = (sellersData as any)?.data?.sellers || [];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-background to-background" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />

        <div className="relative max-w-7xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/30 bg-gold/5 mb-8">
            <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
            <span className="text-gold text-sm uppercase tracking-wider">The Frontier of Digital Goods</span>
          </div>

          {/* Title */}
          <h1 className="font-western text-5xl md:text-7xl lg:text-8xl mb-6">
            <span className="text-cream">Welcome to the</span>
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-cream via-gold to-dust">
              OutlawOasis
            </span>
          </h1>

          {/* Divider */}
          <div className="w-20 h-px mx-auto bg-gradient-to-r from-transparent via-gold to-transparent mb-6" />

          {/* Subtitle */}
          <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto mb-10">
            The premier marketplace for digital goods. Quality products from verified sellers, secure payments, and instant delivery.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/products" className="btn-primary px-8 py-4 text-base">
              Browse Products
            </Link>
            <Link to="/sellers/apply" className="btn-secondary px-8 py-4 text-base">
              Become a Seller
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-western text-3xl text-cream mb-2">Trending Products</h2>
              <p className="text-text-secondary">Top-selling items from our marketplace</p>
            </div>
            <Link to="/products" className="text-gold hover:text-gold-bright transition-colors">
              View all &rarr;
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product: any) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="card group overflow-hidden"
              >
                {/* Image */}
                <div className="aspect-video bg-leather relative overflow-hidden">
                  {product.thumbnailUrl ? (
                    <img
                      src={product.thumbnailUrl}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-gold/30 font-western text-2xl">OO</span>
                    </div>
                  )}
                  {product.compareAtPrice && (
                    <span className="absolute top-2 right-2 bg-hot text-white text-xs px-2 py-1 rounded">
                      Sale
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-text-muted text-xs mb-1">{product.seller.storeName}</p>
                  <h3 className="font-medium text-cream mb-2 line-clamp-1">{product.name}</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-gold font-semibold">{formatPrice(product.price)}</span>
                      {product.compareAtPrice && (
                        <span className="text-text-muted line-through text-sm">
                          {formatPrice(product.compareAtPrice)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-text-muted text-sm">
                      <span>&#9733;</span>
                      <span>{product.ratingAverage?.toFixed(1) || '0.0'}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Sellers */}
      <section className="py-20 px-4 bg-leather/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-western text-3xl text-cream mb-2">Top Sellers</h2>
              <p className="text-text-secondary">Verified sellers with quality products</p>
            </div>
            <Link to="/sellers" className="text-gold hover:text-gold-bright transition-colors">
              View all &rarr;
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sellers.map((seller: any) => (
              <Link
                key={seller.id}
                to={`/sellers/${seller.storeSlug}`}
                className="card p-6 text-center group"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-surface border-2 border-gold/30 flex items-center justify-center overflow-hidden">
                  {seller.storeLogoUrl ? (
                    <img src={seller.storeLogoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-gold font-western text-2xl">
                      {seller.storeName[0]}
                    </span>
                  )}
                </div>
                <h3 className="font-medium text-cream mb-1 group-hover:text-gold transition-colors">
                  {seller.storeName}
                </h3>
                <p className="text-text-muted text-sm mb-3">@{seller.user.username}</p>
                <div className="flex items-center justify-center gap-4 text-sm text-text-secondary">
                  <span>{seller.totalSales} sales</span>
                  <span>&#9733; {seller.ratingAverage?.toFixed(1) || '0.0'}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-western text-3xl text-cream mb-4">Why Choose Us</h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              We're building the best marketplace for digital goods
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Verified Sellers',
                description: 'Every seller is vetted through our invite-only system, ensuring quality products.',
                icon: '&#9733;',
              },
              {
                title: 'Secure Payments',
                description: 'Pay with credit cards or cryptocurrency. Your payments are always protected.',
                icon: '&#128274;',
              },
              {
                title: 'Instant Delivery',
                description: 'Get your digital products immediately after purchase. No waiting required.',
                icon: '&#9889;',
              },
            ].map((feature, index) => (
              <div key={index} className="card p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <span className="text-2xl text-gold" dangerouslySetInnerHTML={{ __html: feature.icon }} />
                </div>
                <h3 className="font-western text-xl text-cream mb-3">{feature.title}</h3>
                <p className="text-text-secondary">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-b from-background to-leather">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-western text-4xl text-cream mb-6">
            Ready to Start Selling?
          </h2>
          <p className="text-text-secondary text-lg mb-8">
            Join our exclusive network of sellers and start earning today.
          </p>
          <Link to="/sellers/apply" className="btn-primary px-8 py-4 text-base">
            Apply to Become a Seller
          </Link>
        </div>
      </section>
    </div>
  );
}
